import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {ArrowLeft, ArrowRight, Check, Mail, User, Building2, Briefcase, ShieldCheck, Loader2} from 'lucide-react'
import Nav from '@/components/Nav'
import ProgressBar from '@/components/ProgressBar'
import { questions, totalQuestions } from '@/data/questions'
import { saveAnswers, saveLead, loadAnswers, type LeadInfo } from '@/lib/store'
import { useQuizSubmission } from '@/hooks/useQuizSubmission'
import TierTeaser from '@/components/TierTeaser'
import { getPreview } from '@/lib/preview'

function Quiz() {
  const navigate = useNavigate()
  const [step, setStep] = useState(0) // 0..totalQuestions-1 = questions, totalQuestions = lead form
  const [answers, setAnswers] = useState<Record<string, number>>({})
  const [lead, setLead] = useState<LeadInfo>({
    name: '',
    email: '',
    company: '',
    role: '',
    consent: false,
  })
  const [errors, setErrors] = useState<Partial<Record<keyof LeadInfo, string>>>({})
  const [submitError, setSubmitError] = useState<string | null>(null)
  const { submit, submitting } = useQuizSubmission()

  useEffect(() => {
    const saved = loadAnswers()
    if (saved && Object.keys(saved).length > 0) {
      setAnswers(saved)
    }
  }, [])

  const isLeadStep = step === totalQuestions
  const currentQuestion = !isLeadStep ? questions[step] : null
  const currentAnswer = currentQuestion ? answers[currentQuestion.id] : undefined

  const handleSelect = (qid: string, score: number) => {
    const next = { ...answers, [qid]: score }
    setAnswers(next)
    saveAnswers(next)
  }

  const handleNext = () => {
    if (step < totalQuestions) {
      setStep(step + 1)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  const handleBack = () => {
    if (step > 0) {
      setStep(step - 1)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  const validateLead = () => {
    const e: Partial<Record<keyof LeadInfo, string>> = {}
    if (!lead.name.trim()) e.name = 'Please enter your name'
    if (!lead.email.trim()) e.email = 'Please enter your email address'
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(lead.email)) e.email = 'Please enter a valid email format'
    if (!lead.company.trim()) e.company = 'Please enter your company name'
    if (!lead.consent) e.consent = 'Consent is required'
    setErrors(e)
    return Object.keys(e).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validateLead()) return
    if (submitting) return
    setSubmitError(null)
    saveLead(lead)
    const result = await submit(lead, answers)
    if (!result.ok) {
      // Don't block the user from seeing their results if DB write fails;
      // surface a non-blocking message and continue.
      setSubmitError(
        'We could not save your submission, but your results are ready below.'
      )
    }
    navigate('/results')
  }

  const completedQuestions = Object.keys(answers).length
  const displayCurrent = isLeadStep ? totalQuestions + 1 : step + 1
  const displayTotal = totalQuestions + 1

  return (
    <div className="min-h-screen flex flex-col bg-[#FAFAF7]">
      <Nav />

      <main className="flex-1">
        <div className="max-w-3xl mx-auto px-6 md:px-10 pt-10 md:pt-16 pb-20">
          <ProgressBar current={displayCurrent} total={displayTotal} />

          <div className="mt-12 md:mt-16" key={step}>
            {currentQuestion && (
              <div className="fade-up">
                <div className="font-mono text-xs uppercase tracking-widest text-[#0F766E] mb-4">
                  {currentQuestion.category}
                </div>
                <h1 className="font-display text-3xl md:text-5xl font-medium leading-tight tracking-tight mb-4">
                  {currentQuestion.question}
                </h1>
                {currentQuestion.description && (
                  <p className="text-[#0A0A0A]/60 mb-10">{currentQuestion.description}</p>
                )}

                <fieldset className="space-y-3">
                  <legend className="sr-only">{currentQuestion.question}</legend>
                  {currentQuestion.options.map((opt, idx) => {
                    const isSelected = currentAnswer === opt.score
                    return (
                      <label
                        key={idx}
                        className={`group flex items-start gap-4 p-5 md:p-6 rounded-2xl border cursor-pointer transition-all ${
                          isSelected
                            ? 'border-[#0F766E] bg-[#0F766E]/5 shadow-md shadow-[#0F766E]/10'
                            : 'border-[#E5E5E5] bg-white hover:border-[#0A0A0A]/30 hover:shadow-sm'
                        }`}
                      >
                        <input
                          type="radio"
                          name={currentQuestion.id}
                          value={opt.score}
                          checked={isSelected}
                          onChange={() => handleSelect(currentQuestion.id, opt.score)}
                          className="sr-only"
                        />
                        <div
                          className={`mt-0.5 flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                            isSelected
                              ? 'border-[#0F766E] bg-[#0F766E]'
                              : 'border-[#0A0A0A]/30 group-hover:border-[#0A0A0A]/60'
                          }`}
                          aria-hidden="true"
                        >
                          {isSelected && <Check size={12} className="text-white" strokeWidth={3} />}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-baseline gap-3">
                            <span className="font-mono text-[10px] text-[#0A0A0A]/40">
                              {String.fromCharCode(65 + idx)}
                            </span>
                            <span className="text-base md:text-lg leading-relaxed">{opt.label}</span>
                          </div>
                        </div>
                      </label>
                    )
                  })}
                </fieldset>
              </div>
            )}

            {isLeadStep && (
              <form onSubmit={handleSubmit} className="fade-up" noValidate>
                <div className="font-mono text-xs uppercase tracking-widest text-[#F59E0B] mb-4">
                  Final Step
                </div>
                <h1 className="font-display text-3xl md:text-5xl font-medium leading-tight tracking-tight mb-4">
                  Almost there.<br />Unlock your full report.
                </h1>
                <p className="text-[#0A0A0A]/60 mb-8 leading-relaxed">
                  Here&apos;s a preview of your AI readiness tier. Complete the quick form below
                  to reveal your full diagnostic, category breakdown, and execution roadmap.
                </p>

                {(() => {
                  const preview = getPreview(answers)
                  return preview.isComplete ? <TierTeaser preview={preview} /> : null
                })()}

                <div className="space-y-5">
                  <Field
                    icon={User}
                    label="Full name"
                    required
                    error={errors.name}
                  >
                    <input
                      type="text"
                      value={lead.name}
                      onChange={(e) => setLead({ ...lead, name: e.target.value })}
                      placeholder="Jane Doe"
                      className="w-full bg-transparent outline-none text-base placeholder:text-[#0A0A0A]/30"
                      autoComplete="name"
                    />
                  </Field>

                  <Field
                    icon={Mail}
                    label="Work email"
                    required
                    error={errors.email}
                  >
                    <input
                      type="email"
                      value={lead.email}
                      onChange={(e) => setLead({ ...lead, email: e.target.value })}
                      placeholder="jane@example.com"
                      className="w-full bg-transparent outline-none text-base placeholder:text-[#0A0A0A]/30"
                      autoComplete="email"
                    />
                  </Field>

                  <Field
                    icon={Building2}
                    label="Company"
                    required
                    error={errors.company}
                  >
                    <input
                      type="text"
                      value={lead.company}
                      onChange={(e) => setLead({ ...lead, company: e.target.value })}
                      placeholder="Acme Inc."
                      className="w-full bg-transparent outline-none text-base placeholder:text-[#0A0A0A]/30"
                      autoComplete="organization"
                    />
                  </Field>

                  <Field
                    icon={Briefcase}
                    label="Role (optional)"
                  >
                    <input
                      type="text"
                      value={lead.role}
                      onChange={(e) => setLead({ ...lead, role: e.target.value })}
                      placeholder="Head of Digital Transformation"
                      className="w-full bg-transparent outline-none text-base placeholder:text-[#0A0A0A]/30"
                      autoComplete="organization-title"
                    />
                  </Field>

                  <label className="flex items-start gap-3 p-5 rounded-2xl bg-[#F3F3EE] cursor-pointer">
                    <input
                      type="checkbox"
                      checked={lead.consent}
                      onChange={(e) => setLead({ ...lead, consent: e.target.checked })}
                      className="sr-only"
                    />
                    <div
                      className={`mt-0.5 flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition ${
                        lead.consent ? 'border-[#0F766E] bg-[#0F766E]' : 'border-[#0A0A0A]/30 bg-white'
                      }`}
                      aria-hidden="true"
                    >
                      {lead.consent && <Check size={12} className="text-white" strokeWidth={3} />}
                    </div>
                    <div className="flex-1 text-sm text-[#0A0A0A]/80 leading-relaxed">
                      <ShieldCheck size={14} className="inline text-[#0F766E] mr-1 -mt-0.5" />
                      <span>
                        I agree to receive my assessment results and related communications.
                        Read our <a href="/privacy" target="_blank" rel="noopener" className="underline text-[#0F766E] hover:text-[#0B524C]">Privacy Policy</a> for details.
                      </span>
                      {errors.consent && <span className="block text-red-600 mt-1 text-xs">{errors.consent}</span>}
                    </div>
                  </label>
                  {submitError && (
                    <p className="text-xs text-red-600 mt-1" role="alert">{submitError}</p>
                  )}
                </div>
              </form>
            )}
          </div>

          {/* Navigation */}
          <div className="mt-12 flex items-center justify-between">
            <button
              type="button"
              onClick={handleBack}
              disabled={step === 0}
              className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-[#0A0A0A]/60 hover:text-[#0F766E] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <ArrowLeft size={14} />
              Back
            </button>

            {!isLeadStep ? (
              <button
                type="button"
                onClick={handleNext}
                disabled={currentAnswer === undefined}
                className="inline-flex items-center gap-3 bg-[#0A0A0A] text-[#FAFAF7] px-7 py-4 rounded-full font-medium text-sm hover:bg-[#0F766E] transition-all disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-[#0A0A0A]"
              >
                {step === totalQuestions - 1 ? 'View results' : 'Next'}
                <ArrowRight size={16} />
              </button>
            ) : (
              <button
                type="submit"
                onClick={handleSubmit}
                disabled={submitting}
                className="inline-flex items-center gap-3 bg-[#0F766E] text-white px-7 py-4 rounded-full font-medium text-sm hover:bg-[#0B524C] transition-all disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Saving…
                  </>
                ) : (
                  <>
                    View results
                    <ArrowRight size={16} />
                  </>
                )}
              </button>
            )}
          </div>

          <div className="mt-8 font-mono text-[10px] uppercase tracking-widest text-[#0A0A0A]/30 text-center">
            {completedQuestions} of {totalQuestions} questions answered
          </div>
        </div>
      </main>
    </div>
  )
}

interface FieldProps {
  icon: React.ComponentType<{ size?: number; className?: string }>
  label: string
  required?: boolean
  error?: string
  children: React.ReactNode
}

function Field({ icon: Icon, label, required, error, children }: FieldProps) {
  return (
    <div>
      <label className={`block bg-white rounded-2xl border transition-all ${error ? 'border-red-400' : 'border-[#E5E5E5] focus-within:border-[#0F766E] focus-within:shadow-md focus-within:shadow-[#0F766E]/10'}`}>
        <div className="flex items-center gap-3 px-5 py-4">
          <Icon size={18} className="text-[#0A0A0A]/40 flex-shrink-0" />
          <div className="flex-1">
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#0A0A0A]/50 mb-0.5">
              {label} {required && <span className="text-[#F59E0B]">*</span>}
            </div>
            {children}
          </div>
        </div>
      </label>
      {error && <p className="text-xs text-red-600 mt-1.5 ml-2">{error}</p>}
    </div>
  )
}

export default Quiz
